'use client'

import { ServiceItem } from '@/lib/types/operations/service'
import { convertCurrency, DEFAULT_CURRENCY } from '@/lib/services/currency/conversion'
import { usePreferences } from '@/hooks/use-preferences'
import { Currency } from '@prisma/client'

// Graceful degradation: this hook only has a vehicleDefinitionId from user preferences,
// not the full make/model/year required by estimateDetailed. Until preferences expose
// those fields directly, we return base price with no multipliers.
// TODO (tracked separately): load preferred vehicle make/model/year into preferences
// so we can call estimateDetailed here.

interface ServicePricingWithPreferredVehicle {
  basePrice: number
  basePriceCAD: number
  calculatedPrice: number
  calculatedPriceCAD: number
  userCurrency: Currency
  exchangeRate: number
  vehicleMultiplier: number
  isCalculating: boolean
  hasMultipliers: boolean
  preferredVehicleDisplay?: string
}

export function useServicePricingWithPreferredVehicle(
  service: ServiceItem
): ServicePricingWithPreferredVehicle {
  // Get user preferences
  const { preferences } = usePreferences()
  const userCurrency = (preferences?.ui?.currency as Currency) || DEFAULT_CURRENCY

  const basePriceCAD = service?.price || 0
  const basePrice = convertCurrency(basePriceCAD, DEFAULT_CURRENCY, userCurrency)
  const exchangeRate = userCurrency === DEFAULT_CURRENCY ? 1.0 : (basePriceCAD > 0 ? basePrice / basePriceCAD : 1.0)

  // Graceful degradation: we don't have make/model/year for the preferred vehicle,
  // so accurate multiplier-based pricing is unavailable. Return base price.
  return {
    basePrice,
    basePriceCAD,
    calculatedPrice: basePrice,
    calculatedPriceCAD: basePriceCAD,
    userCurrency,
    exchangeRate,
    vehicleMultiplier: 1.0,
    isCalculating: false,
    hasMultipliers: false,
    preferredVehicleDisplay: undefined
  }
}
